import React, { useMemo, useState } from 'react';

import { Alert, Checkbox, Table } from 'antd';

import styled from 'styled-components';

import { capitalize } from '@libs/NwUtils';

import { getContactName, getContactType } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import BillingInfo from '@components/BillingInfo/BillingInfo';
import Money from '@components/Gui/Money';
import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import CreateInvoiceDrawer from '@components/InvoicesAndCreditNotes/CreateInvoiceDrawer';
import { adaptBillingSearchStatements } from '@components/StatementRowsCommon/adapters';
import {
    ClientStatementStatusCell,
    StatementAmountCell,
    StatementActionsCell,
    StatementDateCell,
    StatementProjectCell,
    SubjectStatementStatusCell,
    StatementTitleCell,
} from '@components/StatementRowsCommon/cells';
import {
    buildInvoiceDraftData,
    groupDisplayedStatementTotalsByCurrency,
    hasMixedCurrenciesForInvoice,
    isMainStatementLine,
} from '@components/StatementRowsCommon/helpers';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const BillingInfoSearchItemContainer = styled.div`
    &.with-table-header {
        .transactions-container {
            .ant-table-thead {
                > tr > th {
                    color: ${(props) => props.theme.TextColorMuted};
                    font-size: ${(props) => props.theme.FontSizeSm};
                    font-weight: 400;
                    text-transform: uppercase;
                }
            }
        }
    }
`;

const BillingInfoSearchItem = ({
    contact,
    statementType,
    onCreateInvoice,
    onEditTransaction,
    readOnly,
    selectionEnabled = true,
    showJobFeeAmount = false,
    showBothStatuses = false,
    showTableHeader = false,
    singleCellDetails = false,
    statementDirection = 'payable',
}) => {
    const [selectedStatementIDs, setSelectedStatementIDs] = useState([]);
    const [indeterminate, setIndeterminate] = useState(false);
    const [checkAll, setCheckAll] = useState(false);
    const [showCreateInvoiceDrawer, setShowCreateInvoiceDrawer] = useState(false);
    const [currentInvoiceData, setCurrentInvoiceData] = useState();

    const configurations = useConfigurations();
    const statementRows = useMemo(() => adaptBillingSearchStatements(contact.Statements || []), [contact.Statements]);

    const clearSelection = () => {
        setSelectedStatementIDs([]);
        setIndeterminate(false);
        setCheckAll(false);
    };

    const someSelected = () => {
        if (!selectionEnabled) {
            return false;
        }
        return statementRows.some((statement) => selectedStatementIDs.includes(statement.ID));
    };

    const checkDifferentCurrencies = () => {
        if (!selectionEnabled) {
            return false;
        }
        const selectedStatements = statementRows.filter((statement) => selectedStatementIDs.includes(statement.ID));
        return hasMixedCurrenciesForInvoice(selectedStatements, configurations.defaultCurrency);
    };

    const prepareInvoiceData = () => {
        setShowCreateInvoiceDrawer(false);
        const selectedStatements = statementRows.filter((statement) => selectedStatementIDs.includes(statement.ID));

        const contactType = getContactType(contact.ContactID);

        const ret = buildInvoiceDraftData({
            statements: selectedStatements,
            contactID: contact.ContactID,
            contactName: contact.ContactName,
            contactTypeLabel: capitalize(contactType.name),
            billingInfo: contact.BillingInfo,
            defaultCurrency: configurations.defaultCurrency,
        });

        setCurrentInvoiceData(ret);
        setShowCreateInvoiceDrawer(true);
    };

    const isStatementBillable = (statement) => {
        return !statement.isInvoiced;
    };

    //const statementIDsArray = billingInfo.Transactions.filter(t => !t.isInvoiced).map(t => t.Statements.map(s => s.ID)).flat()
    const statementIDsArray = statementRows.filter(isStatementBillable).map((s) => s.ID);
    const statementsNumber = statementIDsArray.length;

    const onSelectChange = (selectedRowKeys, selectedRows) => {
        setSelectedStatementIDs(selectedRowKeys);
        setIndeterminate(selectedRowKeys.length > 0 && selectedRowKeys.length < statementsNumber);
        setCheckAll(selectedRowKeys.length === statementsNumber);
    };

    const onCheckAllChange = (e) => {
        const selectedKeys = e.target.checked ? statementIDsArray : [];
        setSelectedStatementIDs([...selectedKeys]);
        setIndeterminate(false);
        setCheckAll(e.target.checked);
    };

    const rowSelection = selectionEnabled
        ? {
              selectedRowKeys: selectedStatementIDs,
              renderCell: function (checked, record, index, originNode) {
                  return (
                      <div className="selection-wrapper">
                          {/* <div><StatementStatusIcon statement={record} /></div> */}
                          <div>{originNode}</div>
                      </div>
                  );
              },
              onChange: onSelectChange,
              getCheckboxProps: (record) => {
                  return { disabled: record.Transaction.Family === 'JobFee' && isMainStatementLine(record) };
              },
          }
        : undefined;

    const statementColumns = [
        {
            title: 'Date',
            key: 'Date',
            dataIndex: 'Date',
            className: 'cell-border-left cell-border-right',
            render: (text, record) => <StatementDateCell row={record} variant="billing-search" />,
        },
        {
            title: 'Client',
            dataIndex: 'ClientStatus',
            key: 'ClientStatus',
            className: 'cell-border-right',
            render: (text, record) => (
                <ClientStatementStatusCell
                    transaction={record.Transaction}
                    detailContactType={statementType?.toLowerCase()}
                />
            ),
        },
        {
            title: 'Status',
            dataIndex: 'Payment',
            key: 'Payment',
            className: 'cell-border-right',
            render: (text, record) => (
                <SubjectStatementStatusCell
                    statement={record}
                    mode="statement-with-payment"
                    statementDirection={statementDirection}
                    detailContactType={statementType?.toLowerCase()}
                />
            ),
        },
        {
            title: singleCellDetails ? 'Details' : 'Title',
            key: 'Title',
            dataIndex: 'Title',
            width: singleCellDetails ? '65%' : '30%',
            render: (text, record) => {
                if (singleCellDetails) {
                    return (
                        <div className="transaction-meta-wrapper">
                            <StatementTitleCell row={record} statementType={statementType} variant="billing-search" />
                            <StatementProjectCell row={record} />
                        </div>
                    );
                }

                return <StatementTitleCell row={record} statementType={statementType} variant="billing-search" />;
            },
        },
        ...(!singleCellDetails
            ? [
                  {
                      title: 'Project',
                      dataIndex: 'Project',
                      key: 'Project',
                      width: '35%',
                      className: 'top-aligned',
                      render: (text, record) => <StatementProjectCell row={record} />,
                  },
              ]
            : []),
        {
            title: 'Amount',
            dataIndex: 'Amount',
            key: 'Amount',
            width: '40%',
            align: 'right',
            className: 'cell-border-right',
            render: (text, record) => {
                return (
                    <StatementAmountCell
                        row={record}
                        defaultCurrency={configurations.defaultCurrency}
                        variant="billing-search"
                        onEditTransaction={onEditTransaction}
                        showJobFeeAmount={showJobFeeAmount}
                    />
                );
            },
        },
        {
            title: 'Actions',
            dataIndex: 'Actions',
            key: 'Actions',
            align: 'center',
            render: (text, record) => {
                return <StatementActionsCell row={record} onEditTransaction={onEditTransaction} />;
            },
        },
    ];

    if (!showBothStatuses) {
        if (statementDirection === 'receivable') {
            statementColumns.splice(1, 1);
        } else {
            statementColumns.splice(2, 1);
        }
    }

    const titleColumnIndex = statementColumns.findIndex((column) => column.key === 'Title');
    const amountColumnIndex = statementColumns.findIndex((column) => column.key === 'Amount');
    const leadingSummaryColSpan = titleColumnIndex + (selectionEnabled ? 1 : 0);
    const betweenTitleAndAmountColSpan = Math.max(amountColumnIndex - titleColumnIndex - 1, 0);
    const trailingSummaryColSpan = Math.max(statementColumns.length - amountColumnIndex - 1, 0);

    const handleInvoiceCreated = (newInvoiceID) => {
        clearSelection();
        onCreateInvoice(newInvoiceID);
        setShowCreateInvoiceDrawer(false);
        //setShowInvoiceDrawer(newInvoiceID)
    };

    return (
        <BillingInfoSearchItemContainer
            className={`billinginfo-line${showTableHeader ? ' with-table-header' : ''}`}
            key={`billinginfo-${contact.BillingInfoID}`}
        >
            <div className="billinginfo-header">
                {selectionEnabled && (
                    <div className="billinginfo-header-left">
                        <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll} />
                    </div>
                )}
                <div className="billinginfo-header-middle">
                    <h2>{contact.ContactName}</h2>
                </div>
                <div className="billinginfo-header-middle2">
                    <BillingInfo data={contact.BillingInfo} contactName={contact.ContactName} inline />
                </div>
            </div>
            <div
                className={`transactions-container transactions-container-statements${
                    readOnly || !selectionEnabled ? ' read-only' : ''
                }`}
            >
                <Table
                    rowSelection={selectionEnabled ? rowSelection : undefined}
                    showHeader={showTableHeader}
                    pagination={false}
                    columns={statementColumns}
                    size="small"
                    dataSource={statementRows}
                    rowKey="ID"
                    summary={(pageData) => {
                        const statementTotals = groupDisplayedStatementTotalsByCurrency(
                            pageData,
                            configurations.defaultCurrency
                        );

                        if (statementTotals.length === 0) {
                            return null;
                        }

                        return (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} style={{ padding: 0, backgroundColor: 'white' }}>
                                    <div className="summary-icon-wrapper">
                                        <NwIcon icon={byPrefixAndName.fass['sigma']} />
                                    </div>
                                </Table.Summary.Cell>
                                {leadingSummaryColSpan > 1 && (
                                    <Table.Summary.Cell
                                        index={1}
                                        colSpan={leadingSummaryColSpan - 1}
                                        style={{ padding: 0, backgroundColor: 'white' }}
                                    />
                                )}
                                <Table.Summary.Cell
                                    index={leadingSummaryColSpan}
                                    style={{ padding: 0, backgroundColor: 'white' }}
                                >
                                    <div className="statement-summary-title">
                                        <small>totals</small>
                                    </div>
                                </Table.Summary.Cell>
                                {betweenTitleAndAmountColSpan > 0 && (
                                    <Table.Summary.Cell
                                        index={leadingSummaryColSpan + 1}
                                        colSpan={betweenTitleAndAmountColSpan}
                                        style={{ padding: 0, backgroundColor: 'white' }}
                                    />
                                )}
                                <Table.Summary.Cell
                                    index={amountColumnIndex + (selectionEnabled ? 1 : 0)}
                                    style={{ padding: 0, backgroundColor: 'white' }}
                                >
                                    <div className="statement-summary-amount-cell">
                                        <div>
                                            {statementTotals.map((item) => (
                                                <Money
                                                    key={item.Currency}
                                                    dineroObj={item.Dinero}
                                                    redIfNegative
                                                    boldIfDefaultCurrency
                                                    style={{ display: 'block', textAlign: 'right' }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </Table.Summary.Cell>
                                {trailingSummaryColSpan > 0 && (
                                    <Table.Summary.Cell
                                        index={amountColumnIndex + (selectionEnabled ? 2 : 1)}
                                        colSpan={trailingSummaryColSpan}
                                        style={{ padding: 0, backgroundColor: 'white' }}
                                    />
                                )}
                            </Table.Summary.Row>
                        );
                    }}
                />
            </div>
            {selectionEnabled && (
                <div className="actions-on-selected-lines">
                    {selectedStatementIDs.length > 0 && (
                        <small className="selected-lines-counter">{selectedStatementIDs.length} selected items</small>
                    )}
                    {someSelected() && (
                        <div className="actions-on-selected-lines-content">
                            {checkDifferentCurrencies() ? (
                                <Alert
                                    message="You can't create an invoice with different currencies"
                                    type="error"
                                    showIcon
                                    style={{ marginTop: '.5rem', marginBottom: '.5rem' }}
                                />
                            ) : (
                                <NwButton
                                    primary
                                    size="small"
                                    label="create invoice"
                                    onClick={() => {
                                        prepareInvoiceData();
                                    }}
                                    disabled={checkDifferentCurrencies()}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
            {showCreateInvoiceDrawer && (
                <CreateInvoiceDrawer
                    onClose={() => setShowCreateInvoiceDrawer()}
                    onInvoiceCreated={handleInvoiceCreated}
                    data={currentInvoiceData}
                />
            )}
        </BillingInfoSearchItemContainer>
    );
};

export default BillingInfoSearchItem;
